import { useState } from 'react';
import { Link } from 'react-router-dom';

// ---------- Types ----------
type StepId = 'who' | 'goal' | 'location' | 'considerations';
type Answers = Partial<Record<StepId, string>>;

interface Option {
  id: string;
  label: string;
  desc: string;
  icon: string;
}

interface Recommendation {
  title: string;
  subtitle: string;
  desc: string;
  path: string;
  icon: string;
  color: string;
  isPrimary: boolean;
  cta: string;
}

// ---------- Data ----------
const WHO_OPTIONS: Option[] = [
  { id: 'individual', label: 'Individual Learner', desc: 'A student or someone ready to grow their creative skills', icon: 'ri-user-3-line' },
  { id: 'parent', label: 'Parent or Guardian', desc: 'Searching for the right program for your child', icon: 'ri-parent-line' },
  { id: 'artist', label: 'Independent Artist', desc: 'A creator or musician ready to level up', icon: 'ri-music-2-line' },
  { id: 'organization', label: 'Organization or Business', desc: 'A church, company, or event needing creative services', icon: 'ri-building-4-line' },
];

const GOAL_OPTIONS: Record<string, Option[]> = {
  individual: [
    { id: 'learn', label: 'Learn Instruments or Vocals', desc: 'Drums, bass, piano, guitar, vocal lessons & more', icon: 'ri-rhythm-line' },
    { id: 'record', label: 'Record & Produce Music', desc: 'Professional studio recording and production', icon: 'ri-record-circle-line' },
    { id: 'content', label: 'Content Creation & Photography', desc: 'Videography, photography, and media production', icon: 'ri-camera-2-line' },
    { id: 'develop', label: 'Overall Creative Development', desc: 'Build confidence, skills, and creative identity', icon: 'ri-seedling-line' },
  ],
  parent: [
    { id: 'learn', label: 'Music & Vocal Lessons', desc: 'Structured lessons for instruments and voice', icon: 'ri-rhythm-line' },
    { id: 'develop', label: 'Creative Development & Confidence', desc: 'Build skills, self-esteem, and creative expression', icon: 'ri-seedling-line' },
    { id: 'neurodivergent', label: 'Neurodivergent-Specific Support', desc: 'Specialized development for neurodivergent children', icon: 'ri-heart-3-line' },
    { id: 'record', label: 'Studio Recording Experience', desc: 'A professional studio session for your child', icon: 'ri-record-circle-line' },
  ],
  artist: [
    { id: 'record', label: 'Record & Release Music', desc: 'Professional studio with full production support', icon: 'ri-record-circle-line' },
    { id: 'learn', label: 'Elevate My Skills', desc: 'Advanced lessons, coaching, and artistry development', icon: 'ri-award-line' },
    { id: 'content', label: 'Content & Visual Media', desc: 'Photography, videos, and branding content', icon: 'ri-camera-2-line' },
    { id: 'consult', label: 'Creative Strategy & Direction', desc: 'Build and elevate your creative brand', icon: 'ri-lightbulb-line' },
  ],
  organization: [
    { id: 'entertainment', label: 'Live Entertainment', desc: 'Live band or piano performance for events', icon: 'ri-music-fill' },
    { id: 'media', label: 'Photography & Video Production', desc: 'Event coverage, reels, streaming, and branded media', icon: 'ri-camera-2-line' },
    { id: 'technical', label: 'Audio & Technical Production', desc: 'Sound systems, AV setup, and church tech installs', icon: 'ri-equalizer-2-line' },
    { id: 'stage', label: 'Stage Production Support', desc: 'Live productions, stage plays, musical direction', icon: 'ri-live-line' },
    { id: 'consulting', label: 'Creative Consulting', desc: 'Team building, systems, and strategic direction', icon: 'ri-lightbulb-line' },
  ],
};

const LOCATION_OPTIONS: Option[] = [
  { id: 'onsite', label: 'At Creative Institute', desc: 'Professional sessions in the Fayetteville, NC area', icon: 'ri-building-line' },
  { id: 'home', label: 'At My Home', desc: 'Sessions come to you — no travel required', icon: 'ri-home-heart-line' },
  { id: 'either', label: 'Either Works for Me', desc: "I'm flexible and open to both options", icon: 'ri-map-pin-2-line' },
];

const CONSIDERATION_OPTIONS: Option[] = [
  { id: 'neurodivergent', label: 'Neurodivergent Learner', desc: 'I or my child needs a patient, tailored approach', icon: 'ri-heart-3-line' },
  { id: 'transportation', label: 'Transportation Challenges', desc: 'Getting to a facility is difficult for us', icon: 'ri-car-line' },
  { id: 'evening', label: 'Need Flexible Scheduling', desc: 'Evening, night, or weekend sessions are important', icon: 'ri-moon-line' },
  { id: 'none', label: 'No Special Considerations', desc: 'Standard scheduling and setup works great', icon: 'ri-checkbox-circle-line' },
];

interface StepConfig {
  id: StepId;
  question: string;
  subtitle: string;
  options: (a: Answers) => Option[];
}

const STEPS: StepConfig[] = [
  { id: 'who', question: 'Who are you?', subtitle: 'Tell us a little about yourself so we can point you in the right direction.', options: () => WHO_OPTIONS },
  { id: 'goal', question: "What's your main goal?", subtitle: 'Pick the outcome that matters most to you right now.', options: (a) => GOAL_OPTIONS[a.who ?? 'individual'] ?? [] },
  { id: 'location', question: 'Where would you prefer your sessions?', subtitle: 'We offer both on-site and in-home options to fit your lifestyle.', options: () => LOCATION_OPTIONS },
  { id: 'considerations', question: 'Any special considerations?', subtitle: 'This helps us tailor our recommendation just for you.', options: () => CONSIDERATION_OPTIONS },
];

// ---------- Logic Helpers ----------
function getVisibleSteps(answers: Answers): StepId[] {
  const steps: StepId[] = ['who', 'goal'];
  if (!answers.who || !answers.goal) return steps;

  const isOrg = answers.who === 'organization';
  const isRecord = answers.goal === 'record';
  const isConsult = answers.goal === 'consult';
  const isNeurodivGoal = answers.goal === 'neurodivergent';

  if (!isOrg && !isRecord && !isConsult && !isNeurodivGoal) {
    steps.push('location');
  }

  const needsConsiderations =
    (answers.who === 'individual' || answers.who === 'parent' || answers.who === 'artist') &&
    !isRecord && !isConsult && !isNeurodivGoal;

  if (needsConsiderations && answers.location) {
    steps.push('considerations');
  }

  return steps;
}

function getRecommendations(answers: Answers): Recommendation[] {
  const { who, goal, location, considerations } = answers;
  const isNeurodivergent = considerations === 'neurodivergent' || goal === 'neurodivergent';
  const needsHome = considerations === 'transportation' || location === 'home';

  if (who === 'organization') {
    return [{
      title: 'Off-Site Services',
      subtitle: 'Professional Creative Services for Organizations',
      desc: 'From live entertainment and technical production to creative consulting — we bring world-class execution wherever you need it.',
      path: '/off-site-services',
      icon: 'ri-sparkling-2-line',
      color: 'from-[#5B21B6] to-[#7C3AED]',
      isPrimary: true,
      cta: 'Explore Off-Site Services',
    }];
  }

  if (goal === 'record') {
    return [{
      title: 'Recording Studio',
      subtitle: 'Industry-Quality Sound Production',
      desc: 'From demos to full albums — professional recording, mixing, mastering, and creative direction in our world-class studio.',
      path: '/recording-studio',
      icon: 'ri-record-circle-line',
      color: 'from-[#0F172A] to-[#1E3A8A]',
      isPrimary: true,
      cta: 'View Studio Packages',
    }];
  }

  if (goal === 'consult') {
    return [{
      title: 'Off-Site Services',
      subtitle: 'Creative Direction & Consulting',
      desc: 'We help artists and creators build, refine, and elevate their creative brand through strategy, consulting, and execution.',
      path: '/off-site-services',
      icon: 'ri-lightbulb-line',
      color: 'from-[#B45309] to-[#D97706]',
      isPrimary: true,
      cta: 'Explore Creative Services',
    }];
  }

  if (isNeurodivergent) {
    const recs: Recommendation[] = [{
      title: 'Neurodivergent Creative Development',
      subtitle: 'Specialized Development Program',
      desc: 'A safe, patient, and personalized program built specifically for neurodivergent individuals — where every learner is seen, supported, and celebrated.',
      path: '/neurodivergent-development',
      icon: 'ri-heart-3-line',
      color: 'from-[#1E3A8A] to-[#5B21B6]',
      isPrimary: true,
      cta: 'Learn About This Program',
    }];
    if (needsHome) {
      recs.push({
        title: 'In-Home Programs',
        subtitle: 'Sessions Delivered to Your Home',
        desc: 'For learners who thrive in familiar environments — we bring the full program experience directly to you, fully equipped.',
        path: '/in-home-programs',
        icon: 'ri-home-heart-line',
        color: 'from-[#065F46] to-[#10B981]',
        isPrimary: false,
        cta: 'Learn About In-Home',
      });
    } else {
      recs.push({
        title: 'On-Site Programs',
        subtitle: 'Structured & Affirming Environment',
        desc: 'Our on-site sessions are built to be safe, structured, and affirming — welcoming for neurodivergent learners at every level.',
        path: '/on-site-programs',
        icon: 'ri-building-line',
        color: 'from-[#1E3A8A] to-[#3730A3]',
        isPrimary: false,
        cta: 'Explore On-Site Programs',
      });
    }
    return recs;
  }

  if (needsHome) {
    return [{
      title: 'In-Home Programs',
      subtitle: 'We Come to You',
      desc: 'Fully equipped, one-on-one sessions delivered directly to your home. All skill levels welcome — no transportation needed.',
      path: '/in-home-programs',
      icon: 'ri-home-heart-line',
      color: 'from-[#1E3A8A] to-[#5B21B6]',
      isPrimary: true,
      cta: 'Explore In-Home Programs',
    }];
  }

  return [{
    title: 'On-Site Programs',
    subtitle: 'Professional & Inclusive Environment',
    desc: 'Structured, hands-on training across music, vocals, content creation, and more — in a professional, inclusive environment that builds real skills.',
    path: '/on-site-programs',
    icon: 'ri-building-line',
    color: 'from-[#1E3A8A] to-[#3730A3]',
    isPrimary: true,
    cta: 'Explore All Programs',
  }];
}

// ---------- Option Card ----------
function OptionCard({ option, selected, onSelect }: { option: Option; selected: boolean; onSelect: () => void }) {
  return (
    <button
      onClick={onSelect}
      className={`text-left p-5 rounded-2xl border-2 transition-all duration-200 cursor-pointer group w-full ${
        selected
          ? 'border-brand-yellow bg-brand-yellow/10'
          : 'border-white/10 bg-white/5 hover:border-white/25 hover:bg-white/8'
      }`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 transition-colors duration-200 ${
          selected ? 'bg-brand-yellow text-brand-blue-dark' : 'bg-white/10 text-white/70 group-hover:bg-white/15'
        }`}>
          <i className={`${option.icon} text-lg`} />
        </div>
        <div className="min-w-0">
          <p className={`font-heading font-bold text-sm mb-1 transition-colors duration-200 leading-snug ${selected ? 'text-brand-yellow' : 'text-white'}`}>
            {option.label}
          </p>
          <p className="font-body text-white/45 text-xs leading-snug">{option.desc}</p>
        </div>
        {selected && (
          <div className="ml-auto flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-brand-yellow text-brand-blue-dark">
            <i className="ri-check-line text-xs" />
          </div>
        )}
      </div>
    </button>
  );
}

// ---------- Results Screen ----------
function ResultsScreen({ recommendations, onReset }: { recommendations: Recommendation[]; onReset: () => void }) {
  const primary = recommendations.find((r) => r.isPrimary);
  const secondary = recommendations.filter((r) => !r.isPrimary);

  // Determine if this is a program-based result (enroll) or a service-based result (contact)
  const isProgramPath = primary && primary.path !== '/recording-studio' && primary.path !== '/off-site-services';
  const enrollPath = isProgramPath ? '/enroll?plan=Standard%20Plan' : '/contact';
  const enrollLabel = isProgramPath ? 'Enroll Now' : 'Request a Quote';
  const enrollIcon = isProgramPath ? 'ri-user-add-line' : 'ri-mail-send-line';

  return (
    <main className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#2D1B69] relative overflow-hidden">
      <div className="absolute top-20 right-20 w-80 h-80 bg-brand-yellow/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 left-10 w-64 h-64 bg-brand-purple/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 pt-28 pb-20 px-8">
        <div className="text-center mb-12">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-brand-yellow mx-auto mb-5">
            <i className="ri-check-double-line text-3xl text-brand-blue-dark" />
          </div>
          <span className="inline-block px-4 py-1.5 rounded-full border border-brand-yellow/50 text-brand-yellow font-body text-xs uppercase tracking-widest bg-brand-yellow/10 mb-4">
            Your Match
          </span>
          <h2 className="font-heading text-white text-4xl lg:text-5xl font-black mb-3">We Found Your Program</h2>
          <p className="font-body text-white/55 text-base max-w-md mx-auto">Based on your answers, here&apos;s what we recommend for you.</p>
        </div>

        <div className="max-w-2xl mx-auto mb-6">
          {primary && (
            <div className={`rounded-3xl bg-gradient-to-br ${primary.color} p-px`}>
              <div className="bg-[#0A1020]/80 rounded-[23px] p-8 backdrop-blur-sm">
                <div className="flex items-start gap-5 mb-5">
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-white/10 text-white flex-shrink-0">
                    <i className={`${primary.icon} text-2xl`} />
                  </div>
                  <div>
                    <span className="font-body text-brand-yellow text-xs uppercase tracking-widest font-semibold">Best Match</span>
                    <h3 className="font-heading text-white text-2xl font-black mt-1 leading-tight">{primary.title}</h3>
                    <p className="font-body text-white/55 text-sm">{primary.subtitle}</p>
                  </div>
                </div>
                <p className="font-body text-white/70 text-sm leading-relaxed mb-6">{primary.desc}</p>
                <Link
                  to={primary.path}
                  className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm cursor-pointer hover:bg-brand-yellow-light transition-all"
                >
                  {primary.cta} <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          )}
        </div>

        {secondary.length > 0 && (
          <div className="max-w-2xl mx-auto mb-8">
            <p className="font-body text-white/35 text-xs uppercase tracking-widest text-center mb-4">Also Consider</p>
            {secondary.map((rec) => (
              <div key={rec.path} className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-3 flex items-start gap-5">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br ${rec.color} text-white flex-shrink-0`}>
                  <i className={`${rec.icon} text-lg`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-heading text-white text-base font-bold mb-1">{rec.title}</h4>
                  <p className="font-body text-white/50 text-xs leading-relaxed mb-3">{rec.desc}</p>
                  <Link to={rec.path} className="whitespace-nowrap inline-flex items-center gap-1.5 text-brand-yellow font-body text-xs font-semibold hover:underline cursor-pointer">
                    {rec.cta} <i className="ri-arrow-right-line text-xs" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="max-w-2xl mx-auto flex flex-col sm:flex-row gap-3 justify-center items-center pt-2">
          <Link
            to={enrollPath}
            className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm hover:bg-brand-yellow-light transition-all cursor-pointer"
          >
            <i className={enrollIcon} /> {enrollLabel}
          </Link>
          <Link to="/contact" className="whitespace-nowrap px-8 py-3.5 rounded-full border-2 border-white/30 text-white font-body font-semibold text-sm hover:border-brand-yellow hover:text-brand-yellow transition-all cursor-pointer">
            Talk to Us First
          </Link>
          <button onClick={onReset} className="whitespace-nowrap px-8 py-3.5 rounded-full text-white/40 font-body font-medium text-sm hover:text-white/65 transition-all cursor-pointer">
            Start Over
          </button>
        </div>
      </div>
    </main>
  );
}

// ---------- Main Quiz Component ----------
export default function ProgramFinderPage() {
  const [answers, setAnswers] = useState<Answers>({});
  const [stepIndex, setStepIndex] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  const visibleSteps = getVisibleSteps(answers);
  const currentStepId = visibleSteps[stepIndex];
  const stepConfig = STEPS.find((s) => s.id === currentStepId);
  const currentOptions = stepConfig?.options(answers) ?? [];
  const progressPct = Math.round((stepIndex / Math.max(visibleSteps.length, 1)) * 100);

  // Determine if selecting the current option would complete the quiz
  const wouldBeLastStep = selectedOption
    ? stepIndex + 1 >= getVisibleSteps({ ...answers, [currentStepId]: selectedOption }).length
    : false;

  const handleSelect = (id: string) => setSelectedOption(id);

  const handleNext = () => {
    if (!selectedOption) return;
    const newAnswers = { ...answers, [currentStepId]: selectedOption };
    setAnswers(newAnswers);
    setSelectedOption(null);
    const newVisible = getVisibleSteps(newAnswers);
    if (stepIndex + 1 >= newVisible.length) {
      setCompleted(true);
    } else {
      setStepIndex(stepIndex + 1);
    }
  };

  const handleBack = () => {
    if (stepIndex === 0) return;
    const newAnswers = { ...answers };
    visibleSteps.slice(stepIndex).forEach((s) => delete newAnswers[s]);
    setAnswers(newAnswers);
    setStepIndex(stepIndex - 1);
    setSelectedOption(null);
  };

  const handleReset = () => {
    setAnswers({});
    setStepIndex(0);
    setCompleted(false);
    setSelectedOption(null);
  };

  if (completed) {
    return <ResultsScreen recommendations={getRecommendations(answers)} onReset={handleReset} />;
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#2D1B69] relative overflow-hidden">
      <div className="absolute top-32 right-10 w-80 h-80 bg-brand-yellow/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 left-0 w-72 h-72 bg-brand-purple/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none select-none">
        <img
          src="https://static.readdy.ai/image/b0ab6c1eb002a8cf89683099ba8ee0be/a9a19e6966780984f72576a4f2fc1316.png"
          alt=""
          className="absolute top-0 right-0 w-[500px] h-auto object-contain opacity-[0.06] mix-blend-screen"
        />
      </div>

      <div className="relative z-10 pt-28 pb-20 px-8">
        {/* Hero */}
        <div className="text-center mb-10">
          <span className="inline-block px-4 py-1.5 rounded-full border border-brand-yellow/50 text-brand-yellow font-body text-xs uppercase tracking-widest bg-brand-yellow/10 mb-5">
            Program Finder
          </span>
          <h1 className="font-heading text-white text-4xl lg:text-5xl font-black mb-3">Find Your Perfect Program</h1>
          <p className="font-body text-white/55 text-base max-w-md mx-auto">
            Answer a few quick questions and we&apos;ll match you to the right Creative Institute experience.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="font-body text-white/45 text-xs">Step {stepIndex + 1} of {visibleSteps.length}</span>
            <span className="font-body text-white/45 text-xs">{progressPct}% complete</span>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-yellow rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        {/* Card */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white/[0.06] border border-white/10 rounded-3xl p-8 backdrop-blur-sm">
            <h2 className="font-heading text-white text-2xl lg:text-3xl font-black mb-2 leading-snug">
              {stepConfig?.question}
            </h2>
            <p className="font-body text-white/50 text-sm mb-8">{stepConfig?.subtitle}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {currentOptions.map((option) => (
                <OptionCard
                  key={option.id}
                  option={option}
                  selected={selectedOption === option.id}
                  onSelect={() => handleSelect(option.id)}
                />
              ))}
            </div>

            <div className="flex items-center justify-between">
              {stepIndex > 0 ? (
                <button
                  onClick={handleBack}
                  className="whitespace-nowrap flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/20 text-white/60 font-body text-sm font-medium hover:border-white/40 hover:text-white transition-all cursor-pointer"
                >
                  <i className="ri-arrow-left-line" /> Back
                </button>
              ) : <div />}

              <button
                onClick={handleNext}
                disabled={!selectedOption}
                className={`whitespace-nowrap flex items-center gap-2 px-8 py-3 rounded-full font-body font-bold text-sm transition-all ${
                  selectedOption
                    ? 'bg-brand-yellow text-brand-blue-dark hover:bg-brand-yellow-light cursor-pointer'
                    : 'bg-white/10 text-white/25 cursor-not-allowed'
                }`}
              >
                {wouldBeLastStep ? 'See My Match' : 'Continue'}
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>

          <p className="font-body text-white/25 text-xs text-center mt-6">
            Not sure? <Link to="/contact" className="text-brand-yellow/60 hover:text-brand-yellow underline cursor-pointer">Contact us</Link> and we&apos;ll help you personally.
          </p>
        </div>
      </div>
    </main>
  );
}
